import {
    NCoreUIKitToast
} from "ncore-ui-kit";
import {
    type ActionResponseTypes,
    type ServiceContextType,
    type ActionRequestTypes,
    type ActionsType,
    type ServiceType
} from "../types";
import moment from "moment";
import NContext, {
    type ConfigType
} from "ncore-context";
import OneSignalService from "../../OneSignalService";
import {
    storage
} from "../../../storage";
import actions from "../actions";
import {
    SERVICE_EVENT_TYPES,
    SERVICE_CONFIGS
} from "../constants";

interface ActionError {
    message?: string;
    code?: number;
    status?: number;
    payload?: {
        isMaintenance?: boolean;
    };
}

class ServiceContextInheritence extends NContext<ServiceContextType<ActionsType>, ConfigType<ServiceContextType<ActionsType>>> {
    tokenRefreshDate: Date = moment(new Date()).add(-100, "day").toDate();
    isTokenRefreshing = false;
    actions;
    queue: Array<{
        actionName: ActionsType;
        params: unknown;
    }> = [];

    constructor(initialState: ServiceType<ActionsType>) {
        super(
            initialState,
            {
                key: "service-context"
            }
        );

        this.actions = initialState.actions;
    }

    emitWithKey = (key: string, payload?: unknown) => {
        this.subscribers.forEach(subscriber => {
            if (subscriber.key === key) subscriber.func(payload, this.state);
        });
    };

    removeAllQueueListeners = () => {
        this.queue.forEach((queueItem) => {
            this.removeEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${queueItem.actionName}`);
            this.removeEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${queueItem.actionName}-error`);
        });
    };

    startRefreshToken = () => {
        this.isTokenRefreshing = true;

        this.actions["RefreshToken"]()
            .then(() => {
                this.isTokenRefreshing = false;

                this.queue.forEach(queueItem => {
                    this.emitWithKey(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${queueItem.actionName}`, true);
                    this.emitWithKey("REFRESH_TOKEN_TRIGGER");
                });
                this.queue = [];
            })
            .catch(() => {
                this.isTokenRefreshing = false;

                this.queue.forEach(queueItem => {
                    this.emitWithKey(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${queueItem.actionName}-error`, false);
                });
                this.removeAllQueueListeners();
                this.queue = [];
            });
    };

    countOfActionInQueue = (actionName: ActionsType) => {
        let count = 0;

        for (let i = 0; i < (this.queue?.length || 0); i++) {
            if (this.queue?.[i]?.actionName === actionName) {
                count += 1;
            }
        }

        return count;
    };

    addQueue = <K extends ActionsType>(actionName: K, params: ActionRequestTypes[K]) => {
        if (!this.isTokenRefreshing) {
            this.startRefreshToken();
        }

        return new Promise((resolve, reject) => {
            const actionCount = this.countOfActionInQueue(actionName);

            if (actionCount > 0) {
                console.log("network-waiting");
                return;
            }

            let timeout: ReturnType<typeof setTimeout> | null = setTimeout(() => {
                reject(false);
            }, 30000);

            this.queue.push({
                actionName: actionName,
                params: params
            });

            const successQueueTriggered = () => {
                this.tokenRefreshDate = new Date();
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }

                this.removeEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${actionName}`);
                this.removeEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${actionName}-error`);

                resolve(true);
            };

            const errorQueueTriggered = () => {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = null;
                }

                reject(false);
            };

            this.addEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${actionName}`, successQueueTriggered);
            this.addEventListener(`${SERVICE_EVENT_TYPES["REFRESH_TOKEN"]}-${actionName}-error`, errorQueueTriggered);
        });
    };

    action = async <K extends ActionsType>(actionName: K, params: ActionRequestTypes[K]): Promise<Pick<ActionResponseTypes, K>[K]> => {
        return await (this.actions[actionName] as CallableFunction)(params)
            .then((res: unknown) => {
                const response = res as { code?: number; message?: string };
                if(!(response.code === 200 || response.code === 201 || response.code === 202)){
                    NCoreUIKitToast.open({
                        title: response.message || "",
                        type: "warning"
                    });
                }

                return res as Pick<ActionResponseTypes, K>[K];
            })
            .catch(async (err: unknown) => {
                const error = err as ActionError;
                console.log("action err", " action name:", actionName, " params: ", params, " err:", error);
                NCoreUIKitToast.open({
                    title: error.message || "Bilinmeyen Hata",
                    type: "danger"
                });
                if (!SERVICE_CONFIGS[actionName].isWorkedWithQueue) {
                    throw error;
                }

                if ((error.code === 503 || error.status === 503) && error.payload?.isMaintenance) {
                    this.emitWithKey("MAINTENANCE_MODE", true);
                }

                if ((error.code === 401 || error.status === 401)) {
                    if(moment().diff(moment(this.tokenRefreshDate), "seconds") < 10){
                        return await (this.actions[actionName] as CallableFunction)(params);
                    }

                    return await this.addQueue(actionName, params)
                        .then(async () => {
                            return await (this.actions[actionName] as CallableFunction)(params);
                        })
                        .catch(() => {
                            this.emitWithKey(SERVICE_EVENT_TYPES["GO_TO_LOGOUT"], true);
                            this.removeAllQueueListeners();
                            this.queue = [];

                            OneSignalService.logout();
                            storage.clearAll();

                            throw {
                                message: "session-ended"
                            };
                        });
                }

                throw error;
            });
    };
};

const RESTServiceInherit = new ServiceContextInheritence({
    actions: actions
});
export default RESTServiceInherit;
